// src/pages/admin/AdminNotificationSettings.tsx
// 診療リマインド（24h/2h/soon/absent/payment）の管理画面：
// 有効/無効・SMS送信可否・件名/案内文（日本語・中国語）を編集する。
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { Bell, Loader2, RotateCcw, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import classNames from 'classnames';

type ReminderKind = '24h' | '2h' | 'soon' | 'absent' | 'payment' | 'doctor_entered';

interface ReminderConfig {
  enabled: boolean;
  allow_sms: boolean;
  subject_ja: string;
  subject_zh: string;
  intro_ja: string;
  intro_zh: string;
}

const KIND_ORDER: ReminderKind[] = ['24h', '2h', 'soon', 'absent', 'payment', 'doctor_entered'];

const KIND_LABELS: Record<ReminderKind, { title: string; hint: string }> = {
  '24h': { title: '前日リマインド', hint: '診療前日の朝に送信（既定オフ）' },
  '2h':  { title: '2時間前リマインド', hint: '診療開始の約2時間前に送信' },
  'soon': { title: '直前リマインド', hint: '診療開始まで0〜15分。問診票などの未完了ナッジ付き' },
  'absent': { title: '未着催促', hint: '開始0〜15分後、患者が未入室のときのみ送信' },
  'payment': { title: '付款催促', hint: '診療完了・未払いの患者へ。24時間ごとに再送' },
  'doctor_entered': { title: '医師入室通知', hint: '医師が診療室に入室した瞬間、未入室の患者へ送信（開始前後30分以内の予約のみ）' },
};

// バックエンドの上限（system_settings.value は VARCHAR(255) のため）。
// API 応答の limits で上書きされるまでの初期値として使用。
const DEFAULT_SUBJECT_MAX = 80;
const DEFAULT_INTRO_MAX = 200;
const DEFAULT_BRAND_NAME_MAX = 40;

const AdminNotificationSettings: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [savingKind, setSavingKind] = useState<ReminderKind | null>(null);
  const [settings, setSettings] = useState<Record<string, ReminderConfig>>({});
  const [drafts, setDrafts] = useState<Record<string, ReminderConfig>>({});
  const [subjectMax, setSubjectMax] = useState(DEFAULT_SUBJECT_MAX);
  const [introMax, setIntroMax] = useState(DEFAULT_INTRO_MAX);
  const [brandNameMax, setBrandNameMax] = useState(DEFAULT_BRAND_NAME_MAX);
  const [brandName, setBrandName] = useState('');
  const [brandNameDraft, setBrandNameDraft] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/settings/reminders');
      setSettings(res.data.settings);
      setDrafts(res.data.settings);
      setBrandName(res.data.brand_name ?? '');
      setBrandNameDraft(res.data.brand_name ?? '');
      if (res.data.limits) {
        setSubjectMax(res.data.limits.subject_max_len ?? DEFAULT_SUBJECT_MAX);
        setIntroMax(res.data.limits.intro_max_len ?? DEFAULT_INTRO_MAX);
        setBrandNameMax(res.data.limits.brand_name_max_len ?? DEFAULT_BRAND_NAME_MAX);
      }
    } catch (err) {
      console.error('Failed to fetch reminder settings', err);
      toast.error(t('admin.update_failed') || '取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const updateDraft = (kind: ReminderKind, patch: Partial<ReminderConfig>) => {
    setDrafts((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));
  };

  const isDirty = (kind: ReminderKind) =>
    JSON.stringify(settings[kind]) !== JSON.stringify(drafts[kind]);

  const handleSave = async (kind: ReminderKind) => {
    const draft = drafts[kind];
    if (!draft) return;
    setSavingKind(kind);
    try {
      const res = await api.put(`/admin/settings/reminders/${kind}`, draft);
      setSettings((prev) => ({ ...prev, [kind]: res.data }));
      setDrafts((prev) => ({ ...prev, [kind]: res.data }));
      toast.success('保存しました / 已保存');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t('admin.update_failed') || '保存に失敗しました');
    } finally {
      setSavingKind(null);
    }
  };

  const handleSaveBrand = async () => {
    setSavingBrand(true);
    try {
      const res = await api.put('/admin/settings/reminders/brand', { brand_name: brandNameDraft });
      setBrandName(res.data.brand_name);
      setBrandNameDraft(res.data.brand_name);
      toast.success('保存しました / 已保存');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t('admin.update_failed') || '保存に失敗しました');
    } finally {
      setSavingBrand(false);
    }
  };

  const handleReset = async (kind: ReminderKind) => {
    if (!window.confirm('既定値に戻しますか？ / 恢复为默认值？')) return;
    setSavingKind(kind);
    try {
      const res = await api.post(`/admin/settings/reminders/${kind}/reset`);
      setSettings((prev) => ({ ...prev, [kind]: res.data }));
      setDrafts((prev) => ({ ...prev, [kind]: res.data }));
      toast.success('既定値に戻しました / 已恢复默认');
    } catch (err) {
      toast.error(t('admin.update_failed') || '操作に失敗しました');
    } finally {
      setSavingKind(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-coral" />
        <p className="mt-4 text-gray-500 font-medium">読み込み中...</p>
      </div>
    );
  }

  const brandDirty = brandName !== brandNameDraft;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-coral/10 flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-coral" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">リマインド通知設定</h1>
          <p className="text-sm text-gray-500">各リマインドの有効/無効・SMS送信可否・件名/案内文を編集します。SMSは課金されるため、必要な種別にのみ許可してください。</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <label className="block text-xs font-semibold text-gray-500 mb-1">
          医院名（件名・案内文の先頭に【　】付きで表示されます。ここを変更するだけで5種類すべてに反映されます）
          <span className="float-right font-normal text-gray-400">{brandNameDraft.length}/{brandNameMax}</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={brandNameDraft}
            maxLength={brandNameMax}
            onChange={(e) => setBrandNameDraft(e.target.value)}
            className="flex-1 min-w-0 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
          />
          <button
            onClick={handleSaveBrand}
            disabled={savingBrand || !brandDirty}
            className={classNames(
              'flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition shrink-0',
              savingBrand || !brandDirty
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-coral text-white hover:opacity-90',
            )}
          >
            <Save size={13} />
            {savingBrand ? '保存中…' : brandDirty ? '保存する' : '保存済み'}
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {KIND_ORDER.map((kind) => {
          const draft = drafts[kind];
          if (!draft) return null;
          const dirty = isDirty(kind);
          const saving = savingKind === kind;
          return (
            <div key={kind} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono font-bold text-coral bg-coral/10 rounded px-2 py-0.5 shrink-0">{kind}</span>
                    <h2 className="font-bold text-gray-800">{KIND_LABELS[kind].title}</h2>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{KIND_LABELS[kind].hint}</p>
                </div>
                <div className="flex items-center gap-4 flex-wrap min-w-0">
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={draft.enabled}
                      onChange={(e) => updateDraft(kind, { enabled: e.target.checked })}
                      className="w-4 h-4 accent-coral shrink-0"
                    />
                    有効
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={draft.allow_sms}
                      onChange={(e) => updateDraft(kind, { allow_sms: e.target.checked })}
                      className="w-4 h-4 accent-coral shrink-0"
                    />
                    SMS送信を許可（課金あり）
                  </label>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    件名（日本語）
                    <span className="float-right font-normal text-gray-400">{draft.subject_ja.length}/{subjectMax}</span>
                  </label>
                  <input
                    type="text"
                    value={draft.subject_ja}
                    maxLength={subjectMax}
                    onChange={(e) => updateDraft(kind, { subject_ja: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    件名（中国語）
                    <span className="float-right font-normal text-gray-400">{draft.subject_zh.length}/{subjectMax}</span>
                  </label>
                  <input
                    type="text"
                    value={draft.subject_zh}
                    maxLength={subjectMax}
                    onChange={(e) => updateDraft(kind, { subject_zh: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    案内文（日本語）
                    <span className="float-right font-normal text-gray-400">{draft.intro_ja.length}/{introMax}</span>
                  </label>
                  <textarea
                    value={draft.intro_ja}
                    maxLength={introMax}
                    rows={2}
                    onChange={(e) => updateDraft(kind, { intro_ja: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    案内文（中国語）
                    <span className="float-right font-normal text-gray-400">{draft.intro_zh.length}/{introMax}</span>
                  </label>
                  <textarea
                    value={draft.intro_zh}
                    maxLength={introMax}
                    rows={2}
                    onChange={(e) => updateDraft(kind, { intro_zh: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 resize-none"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 mt-4">
                <button
                  onClick={() => handleReset(kind)}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
                >
                  <RotateCcw size={13} />
                  既定値に戻す
                </button>
                <button
                  onClick={() => handleSave(kind)}
                  disabled={saving || !dirty}
                  className={classNames(
                    'flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition',
                    saving || !dirty
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-coral text-white hover:opacity-90',
                  )}
                >
                  <Save size={13} />
                  {saving ? '保存中…' : dirty ? '保存する' : '保存済み'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminNotificationSettings;
